<template>
	<div class="blastdog-import-container">
		<div class="imdex-table-top">
			<div class="imdex-table-title">Select pattern to import</div>
		</div>
		<div class="imdex-table-filters">
			<imdex-text-field
				v-model:value="searchTerm"
				style="max-width: 25%"
				:label="$t(`import.BLASTDOG.searchInputLabel`)"
				leadingIcon="fa-search"
			></imdex-text-field>
			<imdex-datetime-picker
				style="max-width: 25%"
				:label="$t(`import.BLASTDOG.lastUpdatedFromLabel`)"
				v-model:value="startDate"
			/>
			<imdex-datetime-picker
				style="max-width: 25%"
				:label="$t(`import.BLASTDOG.lastUpdatedToLabel`)"
				v-model:value="endDate"
				:min="startDate"
			/>
			<imdex-switch
				:label="$t(`import.BLASTDOG.showOnlyNewLabel`)"
				v-model:value="inclPrevGenPatterns"
			></imdex-switch>
		</div>
		<div v-if="this.isBlastDogDatasetInProgress" class="blastdog-in-progress">
			{{ $t(`import.BLASTDOG.inProgressMsg`) }}
		</div>
		<imdex-table
			v-else
			class="prev-table"
			style="padding-bottom: 55px"
			:tableData="{ tableData, columns, options }"
		>
			<template #h-selected> </template>
			<template #h-name>
				<div>{{ $t(`import.BLASTDOG.tableHeadPatternName`) }}</div>
			</template>
			<template #h-bench>
				<div>{{ $t(`import.BLASTDOG.tableHeadBench`) }}</div>
			</template>
			<template #h-new>
				<div>{{ $t(`import.BLASTDOG.tableHeadNew`) }}</div>
			</template>
			<template #h-last_updated>
				<div>{{ $t(`import.BLASTDOG.tableHeadLastUpdated`) }}</div>
			</template>
			<template #h-status>
				<div>{{ $t(`import.BLASTDOG.tableHeadStatus`) }}</div>
			</template>
			<template #selected="prop">
				<div>
					<imdex-checkbox
						:key="prop.row.name"
						v-model:value="prop.row.selected"
					></imdex-checkbox>
				</div>
			</template>
			<template #last_updated="prop">
				<div>{{ moment(prop.row.last_updated).format("MM/DD/YYYY") }}</div>
			</template>
		</imdex-table>
		<div class="import-dialog-actions">
			<div class="action-detail-text">
				{{ selectedPatterns.length }} {{ $t(`import.BLASTDOG.patternSelectedTxt`) }}
			</div>
			<imdex-btn @click="$emit('cancel')" text>{{
				$t(`import.BLASTDOG.cancelBtn`)
			}}</imdex-btn>
			<imdex-btn
				@click="submitCreateDataset"
				:disabled="creatingDataset || selectedPatterns.length == 0"
				:loading="creatingDataset"
				secondary
				>{{ $t(`import.BLASTDOG.importBtn`) }}</imdex-btn
			>
		</div>
	</div>
</template>
<script type="ts">
import { defineComponent } from "vue";
import moment from "moment";
import { useApiStore } from "@/stores/apiStore";
import { useSiteStore } from "@/stores/siteStore";
import { storeToRefs } from "pinia";
import { useToastStore } from "@/stores/toastStore";

export default defineComponent({
    name: "imdex-blastdog-import",
    setup() {
        const api = useApiStore();
        const toast = useToastStore();
        const { activeSite } = storeToRefs(useSiteStore());
        return { api, activeSite, toast };
    },
    mounted() {
        this.fetchPatterns();
    },
    props: {
        isBlastDogDatasetInProgress: {
            type: Boolean,
            default: false
        },
        setIsBlastDogDatasetInProgress: {
            type: Function,
            default: Function.prototype
        }
    },
    data() {
        return {
            moment,
            isLoading: false,
            startDate: moment().add(-1, "M").toDate(),
            endDate: new Date(),
            columns: ["selected", "name", "bench", "new", "last_updated", "status"],
            searchTerm: "",
            creatingDataset: false,
            tData: [
                // {name:'Pattern 2',selected:false, bench:'1',new:'2', last_updated:moment().add(-2, 'd').toDate()},
                // {name:'Pattern 3',selected:false, bench:'1',new:'0', last_updated:moment().add(-3, 'd').toDate()},
                // {name:'Pattern 4',selected:false, bench:'1',new:'12', last_updated:moment().add(-4, 'd').toDate()},
                // {name:'Pattern 5',selected:false, bench:'1',new:'2', last_updated:moment().add(-6, 'd').toDate()},
                // {name:'Pattern 6',selected:false, bench:'1',new:'0', last_updated:moment().add(-8, 'd').toDate()},
                // {name:'Pattern 7',selected:false, bench:'1',new:'12', last_updated:moment().add(-20, 'd').toDate()},
                // {name:'Pattern 8',selected:false, bench:'1',new:'2', last_updated:moment().add(-33, 'd').toDate()},
                // {name:'Pattern 9',selected:false, bench:'1',new:'0', last_updated:moment().add(-32, 'd').toDate()},
                // {name:'Pattern 10',selected:false, bench:'1',new:'12', last_updated:moment().add(-26, 'd').toDate()},
                // {name:'Pattern 11',selected:false, bench:'1',new:'2', last_updated:moment().add(-96, 'd').toDate()},
                // {name:'Pattern 12',selected:false, bench:'1',new:'0', last_updated:moment().add(-56, 'd').toDate()}
            ],
            options: {
                columns: {
                    selected: {
                        maxWidth: "3%",
                        sortable: false
                    },
                    name: {
                        minWidth: "45%",
                        sortable: true
                    }
                }
            },
            inclPrevGenPatterns: false
        };
    },
    methods: {
        async fetchPatterns() {
            try {
                const { data } = await this.api.get(
                    `/blastdog/read/patterns?siteId=${this.activeSite.id}${!this.inclPrevGenPatterns ? "&deployableOnly=true" : ""}`
                );
                this.tData = data.map((item) => {
                    return {
                        name: item.pattern_name,
                        selected: false,
                        bench: item.bench_name,
                        new: item.new_holes,
                        last_updated: moment(item.updated_at).add(-1, "d").toDate(),
                        status: item.status
                    };
                });
            } catch (e) {
                console.log(e);
            }
        },
        async submitCreateDataset() {
            try {
                this.creatingDataset = true;
                const post = {
                    siteId: this.activeSite.id,
                    patternNames: this.selectedPatterns
                };

                await this.api.post({ endpoint: "/dataset-blastdog/create/patterns", post });
                this.toast.toast({ message: "", title: this.$t("import.BLASTDOG.toastSuccessTitle"), dismissable: false, type: "success" });
                this.creatingDataset = false;
                this.setIsBlastDogDatasetInProgress(true);
                this.$emit("cancel");
            } catch (e) {
                console.log(e);
                this.toast.toast({ message: "", title: this.$t("import.BLASTDOG.toastErrorTitle"), dismissable: false, type: "error" });
                this.creatingDataset = false;
            }
        }
    },
    computed: {
        tableData: {
            get() {
                return this.tData.filter((item) => {
                    return (
                        item.name.toLowerCase().indexOf(this.searchTerm.toLowerCase()) > -1 &&
                        item.last_updated > new Date(this.startDate) &&
                        item.last_updated < new Date(this.endDate)
                    );
                });
            },
            set(v) {
                console.log(v);
            }
        },
        selectedPatterns: {
            get() {
                return this.tableData.filter(item => item.selected).map(item => item.name);
            }
        }
    },
    watch: {
        isBlastDogDatasetInProgress() {
            this.fetchPatterns();
        },
        inclPrevGenPatterns() {
            this.fetchPatterns();
        }
    }
});
</script>
<style lang="scss">
.blastdog-import-container {
	overflow: auto;
	height: 50vh;
	padding: 24px;
}
.imdex-table-top {
	padding: 15px 0;
	padding-top: 0;
	display: flex;
	align-items: center;
	justify-content: space-between; /* Add this */
}

.imdex-table-title {
	font-size: 20px;
	font-weight: bold;
}

.imdex-table-filters {
	padding: 15px 0;
	display: flex;
	align-items: center;
	gap: 16px;
}
.action-detail-text {
	margin-right: auto;
}
.blastdog-in-progress {
	height: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
}
</style>
